/**
 * EventsTableSection - Filter controls, sortable table, and pagination for events.
 * Extracted from PopupEventsPage for readability.
 */
import React, { useCallback } from 'react';
import {
  Button,
  Card,
  SearchInput,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import { cn, formatVesselName, timeSince } from '@ais-ui/utils';
import {
  PiDotsThreeVertical,
  PiCheckCircle,
  PiWarningCircle,
  PiFunnel,
} from 'react-icons/pi';
import { SortableTableHead } from './shared';
import type { DensityClasses } from './shared';
import { PopupActions } from './PopupEvents';
import type { DetectedEvent } from '@ais-ui/websockets';

/**
 * Map raw severity values to display names
 */
export const mapSeverity = (severity: string): string => {
  switch (severity) {
    case 'WDESEV_NOTE':
      return 'note';
    case 'WDESEV_WARNING':
      return 'warning';
    case 'WDESEV_ALERT':
      return 'alert';
    default:
      return severity || 'unknown';
  }
};

/**
 * Event row dropdown menu - uses PopupActions to communicate with main window
 */
const EventRowMenu: React.FC<{
  event: DetectedEvent;
  messages: Record<string, string>;
}> = ({ event, messages }) => {
  const handleSelectShip = useCallback(() => {
    PopupActions.viewTarget({
      mmsi: event.mmsi?.toString(),
      name: event.shipName,
      imo: event.imo,
      callSign: event.callSign,
    });
  }, [event]);

  const handleShowDetector = useCallback(() => {
    if (event.shapeUid && event.name) {
      PopupActions.showEventShape(event.shapeUid, event.name);
    }
  }, [event.shapeUid, event.name]);

  const handleShowEvent = useCallback(() => {
    PopupActions.gotoEvent(event.id);
  }, [event.id]);

  const handleAcknowledge = useCallback(() => {
    PopupActions.acknowledgeEvent(event.id);
  }, [event.id]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="size-7">
          <PiDotsThreeVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleSelectShip}>
          {messages.popupEventsMenuSelectShip}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleShowDetector}>
          {messages.popupEventsMenuShowDetector}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleShowEvent}>
          {messages.popupEventsMenuShowEvent}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={event.ack_state === 2}
          onClick={handleAcknowledge}
        >
          {messages.popupEventsMenuAcknowledge}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export interface EventsTableSectionProps {
  events: DetectedEvent[];
  totalFilteredCount: number;
  search: string;
  onSearchChange: (value: string) => void;
  severityFilter: string;
  onSeverityFilterChange: (value: string) => void;
  detectorFilter: string;
  onDetectorFilterChange: (value: string) => void;
  uniqueDetectors: string[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  orderBy: string;
  orderAscending: boolean;
  onSort: (column: string) => void;
  isFiltered: boolean;
  densityClasses: DensityClasses;
  formatDateTime: (
    date: Date | null | undefined,
    hideSeconds?: boolean,
  ) => string;
  messages: Record<string, string>;
}

export const EventsTableSection: React.FC<EventsTableSectionProps> = ({
  events,
  totalFilteredCount,
  search,
  onSearchChange,
  severityFilter,
  onSeverityFilterChange,
  detectorFilter,
  onDetectorFilterChange,
  uniqueDetectors,
  page,
  totalPages,
  onPageChange,
  orderBy,
  orderAscending,
  onSort,
  isFiltered,
  densityClasses,
  formatDateTime,
  messages,
}) => {
  return (
    <Card className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className="flex items-center justify-between p-3 border-b flex-shrink-0 gap-2">
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {totalFilteredCount} events
          {isFiltered && (
            <span className="ml-1 text-xs">{messages.popupEventsFiltered}</span>
          )}
        </span>
        <div className="flex items-center gap-2 flex-1 justify-end">
          <SearchInput
            placeholder={messages.popupEventsFindEvents}
            className="h-8 w-40 text-xs"
            value={search}
            onChange={(e) => onSearchChange(e.currentTarget.value)}
            onClear={() => onSearchChange('')}
          />
          <PiFunnel className="h-4 w-4 text-muted-foreground" />
          <Select value={severityFilter} onValueChange={onSeverityFilterChange}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue placeholder={messages.popupEventsTableSeverity} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {messages.popupEventsAllSeverities}
              </SelectItem>
              <SelectItem value="alert">
                {messages.popupEventsSeverityAlert}
              </SelectItem>
              <SelectItem value="warning">
                {messages.popupEventsSeverityWarning}
              </SelectItem>
              <SelectItem value="note">
                {messages.popupEventsSeverityNote}
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={detectorFilter} onValueChange={onDetectorFilterChange}>
            <SelectTrigger className="w-40 h-8 text-xs">
              <SelectValue placeholder={messages.popupEventsTableDetector} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {messages.popupEventsAllDetectors}
              </SelectItem>
              {uniqueDetectors.map((detector) => (
                <SelectItem key={detector} value={detector}>
                  {detector}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Pagination in header */}
          {totalPages > 1 && (
            <>
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {page} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  {messages.popupGenericPrevious}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                >
                  {messages.popupGenericNext}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <Table container={false}>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <SortableTableHead
                column="name"
                orderBy={orderBy}
                orderAscending={orderAscending}
                onSort={onSort}
                className={densityClasses.tableHeader}
                iconSize={densityClasses.icon.small}
                textSize={densityClasses.text}
              >
                {messages.popupEventsTableDetector}
              </SortableTableHead>
              <SortableTableHead
                column="shipName"
                orderBy={orderBy}
                orderAscending={orderAscending}
                onSort={onSort}
                className={densityClasses.tableHeader}
                iconSize={densityClasses.icon.small}
                textSize={densityClasses.text}
              >
                {messages.popupEventsTableShip}
              </SortableTableHead>
              <SortableTableHead
                column="mmsi"
                orderBy={orderBy}
                orderAscending={orderAscending}
                onSort={onSort}
                className={densityClasses.tableHeader}
                iconSize={densityClasses.icon.small}
                textSize={densityClasses.text}
              >
                {messages.popupEventsTableMmsi}
              </SortableTableHead>
              <SortableTableHead
                column="severity"
                orderBy={orderBy}
                orderAscending={orderAscending}
                onSort={onSort}
                className={densityClasses.tableHeader}
                iconSize={densityClasses.icon.small}
                textSize={densityClasses.text}
              >
                {messages.popupEventsTableSeverity}
              </SortableTableHead>
              <SortableTableHead
                column="act_ts"
                orderBy={orderBy}
                orderAscending={orderAscending}
                onSort={onSort}
                className={densityClasses.tableHeader}
                iconSize={densityClasses.icon.small}
                textSize={densityClasses.text}
              >
                {messages.popupEventsTableActivatedAt}
              </SortableTableHead>
              <SortableTableHead
                column="deactivated"
                orderBy={orderBy}
                orderAscending={orderAscending}
                onSort={onSort}
                className={densityClasses.tableHeader}
                iconSize={densityClasses.icon.small}
                textSize={densityClasses.text}
              >
                {messages.popupEventsTableDeactivatedAt}
              </SortableTableHead>
              <TableHead className={densityClasses.tableHeader}>
                {messages.popupEventsTableStatus}
              </TableHead>
              <TableHead className={densityClasses.tableHeader}>
                {messages.popupEventsTableAck}
              </TableHead>
              <TableHead className={densityClasses.tableHeader}></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map((event) => {
              const isActive = !event.deactivated;
              return (
                <TableRow key={event.id} className="odd:bg-muted/50">
                  <TableCell
                    className={cn(
                      densityClasses.tableCell,
                      'font-medium max-w-32 truncate',
                    )}
                  >
                    {event.name}
                  </TableCell>
                  <TableCell
                    className={cn(
                      densityClasses.tableCell,
                      'max-w-32 truncate',
                    )}
                  >
                    {formatVesselName(event.shipName)}
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    {event.mmsi}
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    {(() => {
                      const severity = mapSeverity(event.severity);
                      return (
                        <span
                          className={cn(
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                            severity === 'alert' && 'bg-red-100 text-red-800',
                            severity === 'warning' &&
                              'bg-yellow-100 text-yellow-800',
                            severity === 'note' && 'bg-blue-100 text-blue-800',
                          )}
                        >
                          {severity}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    <div className="flex flex-col">
                      <span className={densityClasses.text}>
                        {formatDateTime(event.act_ts)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {timeSince(event.act_ts)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    {event.deactivated ? (
                      <div className="flex flex-col">
                        <span className={densityClasses.text}>
                          {formatDateTime(event.deactivated)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {timeSince(event.deactivated)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        isActive
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {isActive
                        ? messages.popupEventsStatusActive
                        : messages.popupEventsStatusDeactivated}
                    </span>
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    {event.ack_state === 1 && (
                      <Tooltip>
                        <TooltipTrigger>
                          <PiWarningCircle className="w-5 h-5 text-yellow-600" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {messages.popupEventsAckNotAcknowledged}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {event.ack_state === 2 && (
                      <Tooltip>
                        <TooltipTrigger>
                          <PiCheckCircle className="w-5 h-5 text-green-600" />
                        </TooltipTrigger>
                        <TooltipContent>
                          {messages.popupEventsAckAcknowledged}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell className={densityClasses.tableCell}>
                    <EventRowMenu event={event} messages={messages} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </Card>
  );
};
